/**
 * «Where hires come from» — the aggregate behind the analytics "Hire sources"
 * tab. Asked with the same `Activity.Query` as the "Vacancies" tab.
 *
 * A hire is a shortlist entry that first entered a hired status within the
 * UTC days of the period: one entry, one hire, however many times it moved
 * between hired statuses. Its source is where the candidate
 * came into the system — the profile's source — not how they were added to the
 * vacancy.
 *
 * A retroactive hire is one recorded on the day the candidate was added to the
 * vacancy: the paperwork of someone already taken on. The status history keeps
 * days, so nothing finer tells it apart.
 */

export interface HireCounts {
  readonly hires: number;
  /** Of `hires`, those recorded on the day the candidate was added to the vacancy. */
  readonly retroactive: number;
  /**
   * Median days from being added to the vacancy to being hired, over the hires
   * that are not retroactive; `null` when there are none. May be a half.
   */
  readonly medianDays: number | null;
}

export interface SourceRow extends HireCounts {
  /** `null` for profiles with no source. */
  readonly sourceId: number | null;
  /** The name of the source as the portal sees it; `null` when there is none. */
  readonly name: string | null;
}

export interface RejectReason {
  readonly reasonId: number;
  readonly name: string;
  readonly count: number;
}

/**
 * Shortlist entries that first entered `not_accepted` within the period, by
 * their current reject reason.
 */
export interface Rejects {
  readonly total: number;
  /** Of `total`, those with no reason; they are not among `reasons`. */
  readonly unspecified: number;
  /** Most frequent first. */
  readonly reasons: RejectReason[];
}

export interface Response {
  /** Most hires first. */
  readonly sources: SourceRow[];
  /** Over every hire at once: its median is not a median of the sources' medians. */
  readonly overall: HireCounts;
  readonly rejects: Rejects;
}
